// Documentation search index with full content
export interface SearchResult {
  title: string;
  path: string;
  category: string;
  description: string;
  keywords: string[];
  content: string; // Full page content for deep searching
  sections: string[]; // Section headings within the page
  snippet?: string; // Contextual snippet showing match
  matchedSection?: string; // Section where match was found
  anchor?: string; // Anchor link to specific section
  score?: number; // Search relevance score
}

export const searchIndex: SearchResult[] = [
  // Getting Started
  {
    title: 'Documentation Home',
    path: '/docs',
    category: 'Getting Started',
    description: 'Welcome to Vehix documentation. Learn about features, guides, and support.',
    keywords: ['home', 'welcome', 'start', 'introduction', 'overview'],
    content:
      'Welcome to Vehix documentation. Quick Links. Features. Recall Map, Recall List, Filters, Manual Routing, Route Tracker, Dark Mode, Campaigns, Dealers, Technicians, Profile Settings. About Vehix. Vehix is a comprehensive recall management system. Need Help.',
    sections: ['Quick Links', 'About Vehix', 'Need Help']
  },
  {
    title: 'Getting Started Guide',
    path: '/docs/getting-started',
    category: 'Getting Started',
    description: 'Quick start guide to begin using Vehix for recall management.',
    keywords: ['setup', 'onboarding', 'begin', 'start', 'first steps', 'login'],
    content:
      'Getting Started. Quick Start. Login to Vehix. Navigate to Recall Map. View recalls. Use filters. What Happens Next. Your role determines features. Service Manager, Service Advisor, Technician. Next Steps. Explore Recall Map. Learn Filters System. Read How-To Guides.',
    sections: ['Quick Start', 'What Happens Next', 'Role-Based Features', 'Next Steps']
  },

  // Features
  {
    title: 'Features Overview',
    path: '/docs/features',
    category: 'Features',
    description: 'Overview of all Vehix features and capabilities.',
    keywords: ['features', 'capabilities', 'tools', 'functionality'],
    content:
      'Features Overview. Core Features. Recall Map, Recall List, Filters System, Manual Routing, Route Tracker. Additional Features. Dark Mode, Campaign Management, Dealer Management, Technician Management, Profile Settings. Role-Based Features. Service Manager, Service Advisor, Technician. Getting Started.',
    sections: ['Core Features', 'Additional Features', 'Role-Based Features']
  },
  {
    title: 'Recall Map',
    path: '/docs/features/recall-map',
    category: 'Features',
    description: 'Interactive map showing recall locations and geographic distribution.',
    keywords: [
      'map',
      'location',
      'geographic',
      'visualization',
      'markers',
      'pins',
      'zoom',
      'pan',
      'navigate',
      'cluster',
      'drag'
    ],
    content:
      'Recall Map. Interactive map visualization. View recalls geographically. Zoom in and out. Pan and drag. Click markers. View clusters. Filter by location. Map controls. Zoom controls. Pan navigation. Reset view. Full screen. Map markers. Individual recalls. Clustered recalls. Color coding by status. Interactive popups with VIN, customer, campaign. Understanding the Map. Markers represent recall locations. Clusters show grouped recalls. Colors indicate status. Green completed, blue in progress, red delayed. Zoom to see details. Click markers for information. Use filters to narrow results. Geographic distribution. Understanding Clusters. Large numbers show grouped recalls. Zoom in to expand clusters. Individual markers when zoomed. Click clusters to zoom automatically. Map Navigation Tips. Double click to zoom in. Scroll wheel to zoom. Click and drag to pan. Use zoom controls. Reset button returns to default view. Troubleshooting. Map not loading. Slow performance. Markers not appearing. Filters not working.',
    sections: [
      'What is the Recall Map',
      'Map Controls',
      'Understanding Markers',
      'Clusters',
      'Navigation Tips',
      'Troubleshooting'
    ]
  },
  {
    title: 'Recall List & Search',
    path: '/docs/features/recall-list',
    category: 'Features',
    description: 'Search and browse all recalls with powerful filtering and sorting.',
    keywords: [
      'list',
      'table',
      'search',
      'find',
      'browse',
      'sort',
      'filter',
      'vin',
      'campaign',
      'customer',
      'export',
      'csv'
    ],
    content:
      'Recall List. Searchable table view. All recalls in one place. Search by VIN, customer name, campaign number. Sort by any column. Filter results. Export to CSV. Table columns. VIN, Customer Name, Campaign Number, Status, Date, Dealer, Technician. Using Search. Type VIN to find vehicle. Search customer name. Enter campaign number. Real-time results. Multiple search terms. Clear search. Sorting. Click column headers. Ascending or descending. Multiple column sort. Default sort by date. Sort indicators. Filtering. Use filters at top. Combine with search. Apply multiple filters. Status filters. Date range filters. Location filters. Campaign filters. Exporting Data. Export visible results. CSV format. Include all columns. Filter before export. Use for reporting. Keyboard Shortcuts. Ctrl+F or Cmd+F to focus search. Enter to search. Escape to clear. Arrow keys to navigate. Common Workflows. Find specific vehicle by VIN. Search customer recalls. View all recalls for campaign. Filter by status and date. Export filtered results. Sort by completion date.',
    sections: [
      'What is Recall List',
      'Table Columns',
      'Using Search',
      'Sorting',
      'Filtering',
      'Exporting',
      'Keyboard Shortcuts',
      'Common Workflows'
    ]
  },
  {
    title: 'Filters System',
    path: '/docs/features/filters',
    category: 'Features',
    description:
      'Master the filtering system to find recalls quickly by location, campaign, and more.',
    keywords: [
      'filter',
      'search',
      'narrow',
      'refine',
      'campaign',
      'location',
      'vehicle',
      'status',
      'date',
      'dealer',
      'make',
      'model',
      'year'
    ],
    content:
      'Filters System. Powerful filtering capabilities. Narrow down recalls. Multiple filter types. Combine filters. Where Filters Appear. Recall Map filters. Recall List filters. Manual Routing filters. Filter Categories. Vehicle Details. Make, model, year, VIN. Campaign Information. Campaign number, priority, type. Location Filters. Dealer location, city, state, zip code, radius. Status Filters. Completed, in progress, pending, delayed. Date Filters. Date range, created date, completion date. Technician Filters. Assigned technician, skill level. Using Filters. Click filter section. Select options. Apply filters. Clear filters. Combine multiple. See filtered results. Filter Tips. Start broad then narrow. Use location first. Combine status and date. Save common filters. Clear when done. Common Filter Scenarios. Customer call about vehicle. Find recalls in area. View pending work. Check completed today. Filter by campaign priority. Technician availability. Troubleshooting. Filters not working. No results shown. Too many results. Slow performance.',
    sections: [
      'Where Filters Appear',
      'Filter Categories',
      'Vehicle Details',
      'Campaign Information',
      'Location',
      'Status',
      'Date',
      'Technician',
      'Using Filters',
      'Tips',
      'Common Scenarios',
      'Troubleshooting'
    ]
  },
  {
    title: 'Manual Routing',
    path: '/docs/features/manual-routing',
    category: 'Features',
    description: 'Create and assign routes to technicians for recall completion.',
    keywords: [
      'route',
      'routing',
      'assign',
      'technician',
      'schedule',
      'plan',
      'optimize',
      'create route',
      'batch assign',
      'workload'
    ],
    content:
      'Manual Routing. Create routes for technicians. Assign multiple recalls. Schedule work. Optimize travel. Batch operations. What is a Route. Collection of recalls. Assigned to technician. Scheduled for completion. Optimized for efficiency. Geographic grouping. Creating Routes. Select recalls from map or list. Choose technician. Set schedule date. Review route details. Assign and save. Route Optimization. Group by location. Minimize travel distance. Consider time windows. Balance workload. Account for skill level. Assigning Recalls. Select multiple recalls. Drag to assign. Click assign button. Choose technician. Set completion date. Confirm assignment. Managing Routes. View active routes. Edit existing routes. Reassign recalls. Delete routes. Track progress. Update status. Route Details. Number of stops. Total recalls. Estimated time. Start and end location. Technician assigned. Scheduled date. Best Practices. Group nearby recalls. Balance technician workload. Consider skill requirements. Schedule appropriate time. Communicate with technicians. Monitor route progress. Common Workflows. Create morning route. Schedule next day work. Reassign incomplete. Balance workload. Emergency assignment. Troubleshooting. Cannot create route. Assignment fails. Technician not available. Skill mismatch. Route too large.',
    sections: [
      'What is Manual Routing',
      'Creating Routes',
      'Route Optimization',
      'Assigning Recalls',
      'Managing Routes',
      'Route Details',
      'Best Practices',
      'Common Workflows',
      'Troubleshooting'
    ]
  },
  {
    title: 'Route Preview',
    path: '/docs/features/route-preview',
    category: 'Features',
    description: 'Review and confirm technician routes before execution, and print route sheets.',
    keywords: [
      'preview',
      'review',
      'confirm',
      'print',
      'route sheet',
      'technician',
      'schedule',
      'daily',
      'draft',
      'status',
      'date picker'
    ],
    content:
      'Route Preview. Review and confirm technician routes before execution. Print route sheets for technicians. Date selection. Technician sections. Print functionality. What is Route Preview. Final checkpoint before technicians head out. Review all assigned routes. Confirm assignments are correct. Print route sheets. Shows only draft and in-progress routes. Key Features. Date Selection. Use date picker to view routes for any date. Defaults to today in dealership timezone. Preview upcoming routes. Review historical assignments. Technician Sections. Routes organized by technician. Collapsible sections. Review workload distribution. Print Functionality. Print individual dealer. Print all dealers per technician. Print all routes. Route Status. Draft route planned not confirmed. Confirmed route locked ready. In Progress technician started working. Completed all recalls addressed. Cancelled route not executed. Daily Workflow. Review routes. Make adjustments. Print route sheets. Track progress. Tips. Review early. Balance workloads. Consider skills. Plan ahead. Common Questions. Why dont I see all routes. Can I edit routes from this page. What timezone does date picker use.',
    sections: [
      'What is Route Preview',
      'Key Features',
      'Date Selection',
      'Technician Sections',
      'Print Functionality',
      'Understanding Route Status',
      'Daily Workflow',
      'Tips',
      'Common Questions'
    ]
  },
  {
    title: 'Route Tracker',
    path: '/docs/features/route-tracker',
    category: 'Features',
    description: 'Monitor active routes and track technician progress in real-time.',
    keywords: [
      'track',
      'monitor',
      'progress',
      'status',
      'completion',
      'delayed',
      'active',
      'performance',
      'metrics',
      'dashboard'
    ],
    content:
      'Route Tracker. Monitor active routes. Track technician progress. Real-time updates. Performance metrics. Status indicators. What is Route Tracker. Mission control for recall work. See technician activity. View completed work. Identify delays. Monitor progress. Why Use Route Tracker. See your progress. Spot delays early. Track technician performance. Review past work. How to Access. Open sidebar. Find TRACK section. Click Route Tracker. Understanding Dashboard. Active routes. Completed today. Completion rate. Total routes. Route Status Badges. Green badge completed. Blue badge in progress. Gray badge pending. Red badge delayed. Viewing Route Details. Find the route. Click to expand. Review individual recalls. Filtering by Date Range. Last 7 days. Last 14 days. Last 30 days. Common Questions. Route showing delayed when completed. See assigned technician. Update frequency. Consistently delayed routes. Quick Reference. Green badge no action. Blue badge actively working. Gray badge scheduled future. Red badge contact technician. Next Steps. Creating Routes. Managing Technicians. Recall List.',
    sections: [
      'What is Route Tracker',
      'Why Use',
      'How to Access',
      'Dashboard',
      'Status Badges',
      'Route Details',
      'Date Range',
      'Common Questions',
      'Quick Reference'
    ]
  },
  {
    title: 'Dark Mode',
    path: '/docs/features/dark-mode',
    category: 'Features',
    description: 'Switch between light and dark themes for comfortable viewing.',
    keywords: [
      'theme',
      'dark',
      'light',
      'appearance',
      'mode',
      'color',
      'display',
      'eye strain',
      'battery',
      'night',
      'system mode',
      'toggle'
    ],
    content:
      'Dark Mode. Switch between light and dark themes. Comfortable viewing. Reduce eye strain. Save battery. Personal preference. What is Dark Mode. Changes colors from light to dark background. Easier on eyes in low light. Like reading with comfortable lamp. Why Would I Want Dark Mode. Easier on your eyes. Save battery life. Better in dark environments. Personal preference. How to Switch Themes. Method 1 theme toggle button. Find theme icon. Click to switch. Pick your favorite. Method 2 profile menu. Open your profile. Find appearance settings. Choose your theme. Understanding Three Theme Options. Light Mode white background. Dark Mode dark background. System Mode automatic. Recommended for Most Users. Try System Mode. Set it and forget it. Automatically follows device. What Changes When You Enable. Backgrounds become dark. Text becomes light. Cards and panels adjust. Tables and lists switch. Maps change to dark style. Forms and buttons adapt. Colors Still Make Sense. Red for delays. Green for completed. Blue for in progress. Adjusted for dark backgrounds. Tips for Using Dark Mode. Give eyes time to adjust. Switch based on time of day. Consider your environment. Troubleshooting. Theme not changing. Refresh the page. Check browser settings. Clear your cache. System Mode not detecting. Screenshots help for bugs. Common Questions. Theme choice affects other users no. Dark mode harder to read not if done well. Schedule dark mode automatically yes with system mode. Try It Now. Click sun moon icon. See how it feels. Switch back instantly.',
    sections: [
      'What is Dark Mode',
      'Why Use',
      'How to Switch',
      'Theme Options',
      'What Changes',
      'Colors',
      'Tips',
      'Troubleshooting',
      'Common Questions'
    ]
  },
  {
    title: 'Campaign Management',
    path: '/docs/features/campaigns',
    category: 'Features',
    description: 'Configure and prioritize recall campaigns by urgency and skill level.',
    keywords: [
      'campaign',
      'priority',
      'urgency',
      'skill',
      'mobile',
      'configure',
      'manage',
      'recall number',
      'manufacturer',
      'safety'
    ],
    content:
      'Campaign Management. Configure recall campaigns. Set priorities. Assign skill levels. Enable mobile service. Track campaign progress. What is a Campaign. Manufacturer recall notice. Campaign number like 24V123. Groups affected vehicles. Easier to manage work. Why Campaign Management Matters. Not all recalls equally urgent. Set priorities. Match work to skills. Focus on important campaigns. Enable mobile service. Track progress. How to Access. Open sidebar. Management section. Campaign Management. Viewing Campaigns. List of all campaigns. Campaign number. Number of recalls. Priority level. Skill requirements. Mobile eligibility. Setting Campaign Priority. High priority urgent safety. Medium priority important non critical. Low priority minor can wait. Priority affects routing. Understanding Skill Levels. Level 1 basic any technician. Level 2 intermediate experienced. Level 3 advanced specialized training. Matches work to technician capability. Mobile Service Settings. Enable for mobile campaigns. Completed at customer location. More convenient service. Requires mobile capability. Not all campaigns eligible. Editing Campaign Settings. Find campaign in list. Click edit button. Update priority. Set skill level. Enable mobile. Save changes. Common Workflows. New campaign arrives. Prioritize by urgency. Set skill requirements. Enable mobile if eligible. Monitor progress. Troubleshooting. Cannot edit campaign. Changes not saving. Priority not affecting routes. Skill mismatch.',
    sections: [
      'What is Campaign',
      'Why Matters',
      'How to Access',
      'Viewing Campaigns',
      'Setting Priority',
      'Skill Levels',
      'Mobile Service',
      'Editing Settings',
      'Common Workflows',
      'Troubleshooting'
    ]
  },
  {
    title: 'Dealer Management',
    path: '/docs/features/dealers',
    category: 'Features',
    description: 'Manage dealership locations and control recall visibility.',
    keywords: [
      'dealer',
      'dealership',
      'location',
      'include',
      'exclude',
      'manage',
      'visibility',
      'filter',
      'service area'
    ],
    content:
      'Dealer Management. Manage dealership locations. Control recall visibility. Include or exclude dealers. Service area management. What is Dealer Management. Control which dealerships see recalls. Include dealers in service area. Exclude dealers outside area. Filter recalls by dealer. Manage dealer list. Why Manage Dealers. Focus on service area. Exclude competitors. Control recall visibility. Organize by territory. Service area boundaries. How to Access. Open sidebar. Management section. Dealer Management. Viewing Dealers. List of all dealers. Dealer name. Location. Status included or excluded. Number of recalls. Including Dealers. Find dealer in list. Click include button. Dealer recalls now visible. Appears in filters. Excluding Dealers. Find dealer in list. Click exclude button. Dealer recalls hidden. Does not appear in filters. Understanding Include Exclude. Include dealer recalls visible. Exclude dealer recalls hidden. Only affects your view. Does not delete recalls. Reversible at any time. Common Use Cases. Service area boundaries. Exclude competitor dealers. Focus on territory. Manage workload. Troubleshooting. Changes not applying. Dealer still appearing. Recalls not showing. Performance issues.',
    sections: [
      'What is Dealer Management',
      'Why Manage',
      'How to Access',
      'Viewing Dealers',
      'Including Dealers',
      'Excluding Dealers',
      'Include vs Exclude',
      'Common Use Cases',
      'Troubleshooting'
    ]
  },
  {
    title: 'Technician Management',
    path: '/docs/features/technicians',
    category: 'Features',
    description: 'Add, edit, and manage your technician team and their skill levels.',
    keywords: [
      'technician',
      'tech',
      'mechanic',
      'team',
      'skill',
      'level',
      'manage',
      'add',
      'edit',
      'delete',
      'assign',
      'capability',
      'technical'
    ],
    content:
      'Technician Management. Manage your technician team. Add new technicians. Edit technician information. Set skill levels. Assign mobile capability. Track technician workload. What is Technician Management. Your team roster. Technician profiles. Skill level tracking. Mobile service capability. Assignment history. How to Access. Open sidebar. Management section. Technician Management. Viewing Technicians. List of all technicians. Name and contact. Skill level. Mobile capability. Active assignments. Adding New Technician. Click add technician. Enter name. Set contact info. Choose skill level. Enable mobile if applicable. Save technician. Editing Technician Info. Find technician in list. Click edit button. Update information. Change skill level. Modify mobile capability. Save changes. Skill Levels Explained. Level 1 basic repairs software updates minimal technical judgment. Level 2 intermediate most repairs. Level 3 advanced complex repairs. Affects campaign assignment. Mobile Service Capability. Technician can travel to customer. Requires mobile tools. Enable for mobile campaigns. Not all technicians mobile enabled. Deactivating Technicians. Find technician. Click deactivate. Technician no longer available. Keeps history. Can reactivate later. Technically can change skill level down. Common Workflows. Onboard new technician. Update skill after training. Enable mobile capability. Deactivate when leaves. Review performance. Troubleshooting. Cannot add technician. Changes not saving. Skill level not applying. Mobile not working.',
    sections: [
      'What is Technician Management',
      'How to Access',
      'Viewing Technicians',
      'Adding New',
      'Editing Info',
      'Skill Levels',
      'Mobile Capability',
      'Deactivating',
      'Common Workflows',
      'Troubleshooting'
    ]
  },
  {
    title: 'Profile & Settings',
    path: '/docs/features/profile',
    category: 'Features',
    description: 'Manage your profile, notifications, and personal preferences.',
    keywords: [
      'profile',
      'settings',
      'preferences',
      'notifications',
      'email',
      'account',
      'user',
      'password',
      'contact',
      'sms',
      'alerts'
    ],
    content:
      'Profile Settings. Manage your profile information. Update contact details. Configure notifications. Set preferences. Customize experience. Your Profile. Name and contact information. Email address. Phone number. Role and permissions. Account status. Editable Fields. Update name. Change email. Modify phone. Profile picture. Contact preferences. Notification Settings. Email notifications. SMS alerts. In app notifications. Quiet hours. Notification frequency. Email Notifications. Route assignments. Status updates. Daily summaries. Campaign updates. System alerts. SMS Notifications. Urgent alerts. Route updates. Mobile service. Time sensitive. Optional service. In App Notifications. Real time updates. Task reminders. System messages. Non urgent alerts. Quiet Hours. Set do not disturb times. Evening hours. Weekend settings. Emergency override. Notification Frequency. Immediate every update. Hourly digest. Daily summary. Weekly report. Theme Preferences. Light dark or system mode. Automatic switching. Personal preference. Profile Completion. Update all fields. Verify contact info. Set preferences. Complete profile. Tips for Profile Management. Keep info current. Set appropriate notifications. Use quiet hours. Choose theme preference. Questions. Edit profile info. Change password. Update email. Notification timing. Multiple devices.',
    sections: [
      'Your Profile',
      'Editable Fields',
      'Notification Settings',
      'Email',
      'SMS',
      'In-App',
      'Quiet Hours',
      'Frequency',
      'Theme',
      'Profile Completion',
      'Tips',
      'Common Questions'
    ]
  },

  // Guides
  {
    title: 'How-To Guides',
    path: '/docs/guides',
    category: 'Guides',
    description: 'Step-by-step tutorials and how-to guides for common workflows.',
    keywords: [
      'guide',
      'tutorial',
      'how-to',
      'walkthrough',
      'instructions',
      'help',
      'workflow',
      'step by step'
    ],
    content:
      'How To Guides. Step by step tutorials. Common workflows. Task walkthroughs. Best practices. Getting started guides. Core workflows. View and filter recalls. Create routes. Assign technicians. Track progress. Complete recalls. Advanced topics. Campaign management. Dealer management. Reporting and exports. Troubleshooting. Role specific guides. Service Manager guides. Service Advisor guides. Technician guides. Tips and tricks. Keyboard shortcuts. Efficiency tips. Common scenarios.',
    sections: ['Core Workflows', 'Advanced Topics', 'Role-Specific Guides', 'Tips and Tricks']
  },

  // Support & Policies
  {
    title: 'Support',
    path: '/docs/support',
    category: 'Support',
    description: 'Get help with Vehix. Contact support and find answers to common questions.',
    keywords: [
      'support',
      'help',
      'contact',
      'assistance',
      'questions',
      'faq',
      'email',
      'chat',
      'hours',
      'technical'
    ],
    content:
      'Support. Get help with Vehix. Contact support team. Technical assistance. Frequently asked questions. Report issues. Feature requests. Support Hours. Monday through Friday. 8 AM to 6 PM EST. Closed weekends and holidays. Email Support. support at kebi.ai. Response within 24 hours. Include screenshots. Describe issue clearly. In App Chat. Click chat icon. Real time assistance. Available during support hours. Quick questions. Security Concerns. Report security issues. Email security at kebi.ai. Responsible disclosure. Priority response. Frequently Asked Questions. Login issues. Password reset. Feature access. Performance problems. Data questions. Before Contacting Support. Check documentation. Try troubleshooting steps. Clear browser cache. Try different browser. Restart application. When Contacting Support. Describe the problem. Include error messages. Send screenshots. List steps to reproduce. Mention your role.',
    sections: [
      'Support Hours',
      'Email Support',
      'In-App Chat',
      'Security Concerns',
      'FAQ',
      'Before Contacting',
      'When Contacting'
    ]
  },
  {
    title: 'Privacy Policy',
    path: '/docs/legal/privacy',
    category: 'Legal',
    description: 'Learn how we collect, use, and protect your personal information.',
    keywords: [
      'privacy',
      'data',
      'personal information',
      'security',
      'gdpr',
      'legal',
      'collection',
      'protection',
      'rights'
    ],
    content:
      'Privacy Policy. Information we collect. How we use information. Data protection. Your privacy rights. Information Collection. Account information. Usage data. Technical information. Location data. Communication data. How We Use Your Information. Provide services. Improve platform. Communicate with you. Legal compliance. Security purposes. Data Protection. Encryption. Access controls. Regular backups. Security monitoring. Incident response. Your Rights. Access your data. Correct information. Delete data. Export data. Opt out marketing. Data Retention. Active accounts retained. Deleted accounts purged. Legal requirements. Backup retention. Cookies and Tracking. Essential cookies. Analytics cookies. Preference cookies. Cookie controls. Third Party Services. Service providers. Payment processors. Analytics tools. Security tools. Data Sharing. We do not sell data. Share with consent. Legal requirements. Service providers only. Security Measures. Industry standard encryption. Regular security audits. Employee training. Incident response plan. International Users. Data transfer. Privacy Shield. GDPR compliance. Contact Us. Privacy questions. Data requests. Email privacy at kebi.ai.',
    sections: [
      'Information Collection',
      'How We Use Information',
      'Data Protection',
      'Your Rights',
      'Data Retention',
      'Cookies',
      'Third Party Services',
      'Data Sharing',
      'Security',
      'International Users',
      'Contact'
    ]
  },
  {
    title: 'Terms of Service',
    path: '/docs/legal/terms',
    category: 'Legal',
    description: 'Terms and conditions for using Vehix software and services.',
    keywords: [
      'terms',
      'service',
      'agreement',
      'conditions',
      'legal',
      'contract',
      'usage',
      'license'
    ],
    content:
      'Terms of Service. Agreement to terms. Service description. User accounts. Acceptable use. Intellectual property. Service Description. Recall management platform. Cloud based software. Mobile access. Support services. Updates and maintenance. User Accounts. Account creation. Account security. User responsibilities. Account termination. Acceptable Use. Permitted uses. Prohibited activities. User conduct. Content guidelines. Service availability. Intellectual Property. Vehix owns platform. User owns their data. License to use. Trademark rights. Copyright protection. Payment Terms. Subscription fees. Payment methods. Refund policy. Late payments. Price changes. Service Availability. Uptime commitment. Scheduled maintenance. Emergency maintenance. Service interruptions. Disclaimer of Warranties. Provided as is. No guarantee. User responsibility. Third party services. Limitation of Liability. Liability limits. Excluded damages. Indemnification. Force majeure. Termination. Termination rights. Effect of termination. Data export. Survival of terms. Changes to Terms. Right to modify. Notification of changes. Continued use acceptance. Governing Law. Jurisdiction. Dispute resolution. Arbitration. Class action waiver. Contact Information. Questions about terms. Legal department. Email legal at kebi.ai.',
    sections: [
      'Agreement',
      'Service Description',
      'User Accounts',
      'Acceptable Use',
      'Intellectual Property',
      'Payment',
      'Service Availability',
      'Warranties',
      'Liability',
      'Termination',
      'Changes',
      'Governing Law',
      'Contact'
    ]
  },
  {
    title: 'Acceptable Use Policy',
    path: '/docs/legal/acceptable-use',
    category: 'Legal',
    description: 'Guidelines for appropriate use of Vehix platform and services.',
    keywords: [
      'acceptable use',
      'policy',
      'guidelines',
      'rules',
      'prohibited',
      'legal',
      'conduct',
      'restrictions',
      'technical'
    ],
    content:
      'Acceptable Use Policy. Guidelines for appropriate use. Prohibited activities. User responsibilities. Reporting violations. Purpose. Protect all users. Ensure service quality. Comply with laws. Maintain security. Permitted Uses. Recall management. Team collaboration. Customer service. Reporting and analytics. Mobile service coordination. Prohibited Activities. Illegal activities. Abuse or harassment. Unauthorized access. Service interference. Data misuse. Security violations. Spam or marketing. Intellectual property violation. Impersonation. Malicious software. Illegal Activities. No illegal content. No fraud. No violation of laws. Legal compliance required. Abuse and Harassment. No harassment. No threats. No hate speech. Respectful conduct. Unauthorized Access. No hacking. No credential sharing. No backdoor access. Authorized use only. Service Interference. No denial of service. No overload. No circumvent limits. No reverse engineering. Report technical issues. Data Misuse. No unauthorized export. No data scraping. No competitor use. Respect privacy. Reporting Violations. Report to support. Email abuse at kebi.ai. Include details. Confidential process. Consequences. Warning. Suspension. Termination. Legal action. Questions. Policy questions. Report violations. Email legal at kebi.ai.',
    sections: [
      'Purpose',
      'Permitted Uses',
      'Prohibited Activities',
      'Illegal Activities',
      'Abuse',
      'Unauthorized Access',
      'Service Interference',
      'Data Misuse',
      'Reporting Violations',
      'Consequences',
      'Questions'
    ]
  },
  {
    title: 'Smart Features',
    path: '/docs/legal/smart-features',
    category: 'Legal',
    description: 'How Vehix smart features work and help you make decisions.',
    keywords: [
      'smart features',
      'suggestions',
      'routing',
      'matching',
      'technician',
      'decisions',
      'control',
      'data usage'
    ],
    content:
      "Smart Features. What smart features do. Finding efficient routes. Matching technicians to jobs. Organizing your work. You always make the final decision. Review suggestions. Change anything. Override completely. Use your judgment. Suggestions are not perfect. Based on available information. Doesn't know traffic or weather. Review before acting. How your data is used. We use dealer locations, recall info, technician skills. We don't sell your data. We don't train AI on your data. Your responsibility. Review all suggestions. Make sure assignments make sense. Use professional judgment.",
    sections: [
      'What Smart Features Do',
      'You Make Decisions',
      'Suggestions Not Perfect',
      'Your Data',
      'Your Responsibility'
    ]
  },
  {
    title: 'Cookie Policy',
    path: '/docs/legal/cookies',
    category: 'Legal',
    description: 'How Vehix uses cookies and similar technologies.',
    keywords: ['cookies', 'tracking', 'browser', 'session', 'preferences', 'privacy', 'settings'],
    content:
      "Cookie Policy. What are cookies. Small files stored on your device. Remember preferences. Keep you logged in. Why we use cookies. Keep you logged in. Remember preferences. Keep you secure. Make things faster. Types of cookies. Essential cookies required. Preference cookies helpful. Performance cookies optional. What cookies don't do. Track you across websites. Collect personal information. Share with advertisers. Show targeted ads. Managing cookies. Browser settings. Block or delete cookies. Important note about blocking essential cookies.",
    sections: [
      'What Are Cookies',
      'Why We Use Cookies',
      'Types of Cookies',
      "What Cookies Don't Do",
      'Managing Cookies',
      'Cookie Duration'
    ]
  },
  {
    title: 'Data Processing Agreement',
    path: '/docs/legal/dpa',
    category: 'Legal',
    description: "How we handle your organization's data.",
    keywords: [
      'data processing',
      'dpa',
      'gdpr',
      'organization',
      'data controller',
      'data processor',
      'security',
      'deletion'
    ],
    content:
      'Data Processing Agreement. Who does what. Your organization data controller. Kebi AI data processor. What we do with your data. Only use to provide service. Follow your instructions. Keep it confidential. How we keep data safe. Encryption. Access controls. Security checks. Staff training. Other companies that help us. See service providers page. Security issues. Notification within 72 hours. When you leave. Getting your data. Deleting your data within 90 days.',
    sections: [
      'Our Roles',
      'What We Do With Data',
      'Keeping Data Safe',
      'Other Companies',
      'Your Requests',
      'Security Issues',
      'When You Leave'
    ]
  },
  {
    title: 'Service Providers',
    path: '/docs/legal/service-providers',
    category: 'Legal',
    description: 'Companies that help us run Vehix.',
    keywords: [
      'service providers',
      'subprocessors',
      'third party',
      'supabase',
      'google',
      'openstreetmap',
      'leaflet'
    ],
    content:
      'Service Providers. How we choose partners. Strong security. Only use data for service. Keep data confidential. Our service providers. Supabase stores data securely. Google provides sign-in. OpenStreetMap provides map data. Leaflet displays maps. How we protect your data. Minimum access. Contracts. No selling. Regular review.',
    sections: ['How We Choose Partners', 'Our Service Providers', 'Data Protection', 'Changes']
  },
  {
    title: 'Security Policy',
    path: '/docs/legal/security',
    category: 'Legal',
    description: 'How we protect your data and respond to security issues.',
    keywords: [
      'security',
      'protection',
      'encryption',
      'breach',
      'notification',
      'incident',
      'safe',
      'password'
    ],
    content:
      "Security Policy. How we protect your data. Encryption in transit and at rest. Access controls. Infrastructure security. Regular testing. Your role in security. Strong passwords. Don't share login. Log out when done. Report suspicious activity. If there's a security problem. Immediate response. Notification within 72 hours. What we'll tell you. Recovery. Reporting security issues. Contact security at kebi.ai. What to include. If account compromised.",
    sections: [
      'How We Protect Data',
      'Your Role in Security',
      'Security Issues',
      'Reporting Issues'
    ]
  }
];

// Helper function to generate contextual snippet
function generateSnippet(content: string, searchTerm: string, contextLength: number = 80): string {
  const lowerContent = content.toLowerCase();
  const lowerTerm = searchTerm.toLowerCase();
  const index = lowerContent.indexOf(lowerTerm);

  if (index === -1) return '';

  // Calculate start and end positions for context
  const start = Math.max(0, index - contextLength);
  const end = Math.min(content.length, index + searchTerm.length + contextLength);

  let snippet = content.substring(start, end);

  // Add ellipsis if we're not at the start or end
  if (start > 0) snippet = '...' + snippet;
  if (end < content.length) snippet = snippet + '...';

  return snippet;
}

// Helper function to find which section contains the match
function findMatchingSection(
  content: string,
  sections: string[],
  searchTerm: string
): string | undefined {
  const sentences = content.split(/\.\s+/);
  const lowerTerm = searchTerm.toLowerCase();

  // Find sentence containing the search term
  for (const sentence of sentences) {
    if (sentence.toLowerCase().includes(lowerTerm)) {
      // Check which section this sentence might belong to
      for (const section of sections) {
        if (sentence.includes(section) || content.indexOf(sentence) < content.indexOf(section)) {
          continue;
        }
        const sectionIndex = content.indexOf(section);
        const sentenceIndex = content.indexOf(sentence);

        // If sentence comes after this section heading
        if (sentenceIndex > sectionIndex) {
          return section;
        }
      }
      // If no section found, return first section as default
      return sections[0];
    }
  }

  return undefined;
}

// Helper function to create anchor from section name
function createAnchor(section: string): string {
  return section
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Enhanced search function with content matching and context
export function searchDocs(query: string): SearchResult[] {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const searchTerm = query.toLowerCase().trim();
  const words = searchTerm.split(/\s+/);

  // Score each result
  const scoredResults = searchIndex.map(item => {
    let score = 0;

    // For single word searches, use the full searchTerm
    // For multi-word searches, also check individual words
    const termsToSearch = words.length === 1 ? [searchTerm] : [...words, searchTerm];

    termsToSearch.forEach(term => {
      const termWeight = term === searchTerm ? 1 : 0.6; // Full phrase gets more weight

      // Exact title match (highest priority)
      if (item.title.toLowerCase() === term) {
        score += 100 * termWeight;
      }

      // Title contains query
      if (item.title.toLowerCase().includes(term)) {
        score += 50 * termWeight;
      }

      // Category match
      if (item.category.toLowerCase().includes(term)) {
        score += 30 * termWeight;
      }

      // Description contains query
      if (item.description.toLowerCase().includes(term)) {
        score += 25 * termWeight;
      }

      // Section headings match
      item.sections.forEach(section => {
        if (section.toLowerCase().includes(term)) {
          score += 20 * termWeight;
        }
        if (section.toLowerCase() === term) {
          score += 35 * termWeight;
        }
      });

      // Keywords match
      item.keywords.forEach(keyword => {
        if (keyword.toLowerCase().includes(term)) {
          score += 15 * termWeight;
        }
        // Exact keyword match
        if (keyword.toLowerCase() === term) {
          score += 30 * termWeight;
        }
      });

      // Content match (lower priority but important for deep search)
      const contentLower = item.content.toLowerCase();
      if (contentLower.includes(term)) {
        // Count occurrences in content
        const occurrences = (contentLower.match(new RegExp(term, 'g')) || []).length;
        score += Math.min(occurrences * 5, 40) * termWeight; // Cap at 40 points for content matches
      }
    });

    // Multi-word matching (all words must appear somewhere)
    if (words.length > 1) {
      const allText = [
        item.title,
        item.category,
        item.description,
        ...item.keywords,
        ...item.sections,
        item.content
      ]
        .join(' ')
        .toLowerCase();

      const allWordsMatch = words.every(word => allText.includes(word));
      if (allWordsMatch) {
        score += 35;
      }

      // Large bonus for exact phrase match in content (highest priority for multi-word)
      if (item.content.toLowerCase().includes(searchTerm)) {
        score += 80; // Big boost for exact phrase
      }

      // Bonus for words appearing close together in content (within 20 chars)
      const wordsPattern = words.join('.{0,20}');
      if (new RegExp(wordsPattern, 'i').test(item.content)) {
        score += 50; // Significant boost for proximity
      }
    }

    // Generate contextual snippet and find matching section
    let snippet = '';
    let matchedSection: string | undefined;
    let anchor: string | undefined;

    // Priority 1: Try to find exact phrase match in content
    if (item.content.toLowerCase().includes(searchTerm)) {
      snippet = generateSnippet(item.content, searchTerm);
      matchedSection = findMatchingSection(item.content, item.sections, searchTerm);
      if (matchedSection) {
        anchor = createAnchor(matchedSection);
      }
    }

    // Priority 2: For multi-word searches, try to find where words appear close together
    if (!snippet && words.length > 1) {
      // Look for words appearing within 50 characters of each other
      const contentLower = item.content.toLowerCase();

      // Find the first word
      const firstWordIndex = contentLower.indexOf(words[0]);
      if (firstWordIndex !== -1) {
        // Check if other words appear nearby (within 100 chars)
        const nearbyText = contentLower.substring(firstWordIndex, firstWordIndex + 100);
        const allWordsNearby = words.every(word => nearbyText.includes(word));

        if (allWordsNearby) {
          // Generate snippet from this location
          const start = Math.max(0, firstWordIndex - 40);
          const end = Math.min(item.content.length, firstWordIndex + 140);
          snippet = item.content.substring(start, end);
          if (start > 0) snippet = '...' + snippet;
          if (end < item.content.length) snippet = snippet + '...';

          matchedSection = findMatchingSection(item.content, item.sections, words[0]);
          if (matchedSection) {
            anchor = createAnchor(matchedSection);
          }
        }
      }
    }

    // Priority 3: Check sections for matches
    if (!snippet) {
      for (const section of item.sections) {
        if (section.toLowerCase().includes(searchTerm)) {
          snippet = `Section: ${section}`;
          matchedSection = section;
          anchor = createAnchor(section);
          break;
        }
      }
    }

    // Priority 4: For multi-word, check if any individual word has a strong match
    if (!snippet && words.length > 1) {
      for (const word of words) {
        if (item.content.toLowerCase().includes(word)) {
          snippet = generateSnippet(item.content, word);
          matchedSection = findMatchingSection(item.content, item.sections, word);
          if (matchedSection) {
            anchor = createAnchor(matchedSection);
          }
          break;
        }
      }
    }

    // Priority 5: Fallback to description if no snippet found
    if (!snippet) {
      snippet = item.description;
    }

    return {
      ...item,
      score,
      snippet,
      matchedSection,
      anchor
    };
  });

  // Filter out results with zero score and sort by score
  return scoredResults
    .filter(result => result.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 10); // Return top 10 results
}
